<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'vuex';
import Icon from 'next/icon/Icon.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import EventSidebarSection from './EventSidebarSection.vue';
import DropdownSearch from 'dashboard/components-next/dropdown-search/DropdownSearch.vue';

const props = defineProps({
  eventManager: { type: Object, default: null },
  formattedAgents: { type: Array, default: () => [] },
  selectedEventManager: { type: Object, default: () => ({}) },
  showEventManagerDropdown: { type: Boolean, default: false },
  searchingAgents: { type: Boolean, default: false },
  eventId: { type: String, default: null },
  collaboratorIds: { type: Array, default: () => [] },
  teamIds: { type: Array, default: () => [] },
  canManageCollaborators: { type: Boolean, default: false },
  canManageEventManager: { type: Boolean, default: false },
  readOnly: { type: Boolean, default: false },
});

const emit = defineEmits([
  'updateEventManager',
  'openEventManagerDropdown',
  'closeEventManagerDropdown',
  'searchEventManager',
  'updated',
]);

const store = useStore();
const { t } = useI18n();

const showCollaboratorsDropdown = ref(false);
const savingCollaborators = ref(false);
const expandedTeamIds = ref([]);
const loadingTeamIds = ref([]);
const fetchedTeamIds = ref([]);

const agents = computed(() => store.getters['agents/getAgents'] || []);
const teams = computed(() => store.getters['teams/getTeams'] || []);

const collaboratorUsers = computed(() => {
  const managerId = props.eventManager?.id;
  return agents.value.filter(
    agent => props.collaboratorIds.includes(agent.id) && agent.id !== managerId
  );
});

const collaboratorTeams = computed(() =>
  teams.value.filter(team => props.teamIds.includes(team.id))
);

const eventManagerPhone = computed(
  () => props.eventManager?.phone_number || props.eventManager?.phone || null
);

const hasCollaborators = computed(
  () => collaboratorUsers.value.length > 0 || collaboratorTeams.value.length > 0
);

const isTeamExpanded = teamId => expandedTeamIds.value.includes(teamId);
const isTeamLoading = teamId => loadingTeamIds.value.includes(teamId);
const getTeamMembers = teamId =>
  store.getters['teamMembers/getTeamMembers'](teamId) || [];

const collaboratorOptions = computed(() => {
  const managerId = props.eventManager?.id;
  return [
    ...agents.value
      .filter(agent => agent.id !== managerId)
      .map(agent => ({
        id: `user-${agent.id}`,
        entityId: agent.id,
        type: 'user',
        name: agent.name,
        email: agent.email,
        thumbnail: agent.thumbnail,
        availability_status: agent.availability_status,
      })),
    ...teams.value.map(team => ({
      id: `team-${team.id}`,
      entityId: team.id,
      type: 'team',
      name: team.name,
    })),
  ];
});

const selectedCollaborators = computed(() =>
  collaboratorOptions.value.filter(option =>
    option.type === 'user'
      ? props.collaboratorIds.includes(option.entityId)
      : props.teamIds.includes(option.entityId)
  )
);

onMounted(async () => {
  if (!agents.value.length) await store.dispatch('agents/get');
  if (!teams.value.length) await store.dispatch('teams/get');
});

watch(
  () => props.showEventManagerDropdown,
  managerOpen => {
    if (managerOpen) showCollaboratorsDropdown.value = false;
  }
);

const openCollaboratorEditor = () => {
  if (!props.canManageCollaborators) return;
  emit('closeEventManagerDropdown');
  showCollaboratorsDropdown.value = true;
};

const toggleEventManager = option => {
  const isSameManager = option.id === props.eventManager?.id;
  emit('updateEventManager', isSameManager ? null : option);
};

const toggleTeam = async teamId => {
  if (isTeamExpanded(teamId)) {
    expandedTeamIds.value = expandedTeamIds.value.filter(id => id !== teamId);
    return;
  }

  expandedTeamIds.value = [...expandedTeamIds.value, teamId];
  if (fetchedTeamIds.value.includes(teamId)) return;

  loadingTeamIds.value = [...loadingTeamIds.value, teamId];
  try {
    await store.dispatch('teamMembers/get', { teamId });
    fetchedTeamIds.value = [...fetchedTeamIds.value, teamId];
  } catch {
    expandedTeamIds.value = expandedTeamIds.value.filter(id => id !== teamId);
  } finally {
    loadingTeamIds.value = loadingTeamIds.value.filter(id => id !== teamId);
  }
};

const toggleCollaborator = async option => {
  if (savingCollaborators.value) return;

  const userIds = [...props.collaboratorIds];
  const teamIds = [...props.teamIds];
  const ids = option.type === 'user' ? userIds : teamIds;
  const index = ids.indexOf(option.entityId);
  if (index >= 0) ids.splice(index, 1);
  else ids.push(option.entityId);

  savingCollaborators.value = true;
  try {
    const updatedEvent = await store.dispatch(
      'planningEvents/updateCollaborators',
      {
        id: props.eventId,
        userIds,
        teamIds,
      }
    );
    emit('updated', updatedEvent);
  } catch {
    useAlert(t('PLANNING.SHOW.COLLABORATORS.ERROR'));
  } finally {
    savingCollaborators.value = false;
  }
};
</script>

<template>
  <EventSidebarSection
    :title="t('PLANNING.SHOW.MEMBERS')"
    icon="i-lucide-users"
  >
    <div class="flex flex-col gap-4">
      <div
        v-if="eventManager"
        class="group/manager relative flex items-center gap-3"
      >
        <DropdownSearch
          v-if="showEventManagerDropdown"
          :options="formattedAgents"
          :selected-item="selectedEventManager"
          has-thumbnail
          :is-searching="searchingAgents"
          :title="t('PLANNING.EVENTS.SELECT_MANAGER')"
          :no-search-result="t('PLANNING.SHOW.NO_AGENTS_FOUND')"
          :input-placeholder="t('PLANNING.SHOW.SEARCH_AGENTS')"
          @select="toggleEventManager"
          @close="emit('closeEventManagerDropdown')"
          @search="emit('searchEventManager', $event)"
        />
        <Avatar
          :src="eventManager.thumbnail"
          :name="eventManager.name"
          :size="40"
          :status="eventManager.availability_status"
          rounded-full
        />
        <div class="min-w-0 flex-1">
          <p
            class="m-0 truncate text-sm font-semibold leading-5 text-n-slate-12"
          >
            {{ eventManager.name }}
          </p>
          <p
            v-if="eventManager.email"
            class="m-0 truncate text-xs leading-4 text-black"
          >
            {{ eventManager.email }}
          </p>
          <a
            v-if="eventManagerPhone"
            :href="`tel:${eventManagerPhone}`"
            class="m-0 block truncate text-xs leading-4 text-black hover:text-woot-500"
          >
            {{ eventManagerPhone }}
          </a>
        </div>
        <span
          class="shrink-0 rounded-full bg-woot-50 px-2.5 py-0.5 text-xs font-medium text-woot-600 dark:bg-woot-900/20 dark:text-woot-300"
        >
          {{ t('PLANNING.SHOW.EVENT_MANAGER') }}
        </span>
        <button
          v-if="canManageEventManager"
          class="absolute left-1.5 z-10 hidden h-7 w-7 items-center justify-center rounded-full bg-woot-500 hover:bg-woot-300 text-white group-hover/manager:inline-flex print:hidden"
          :title="t('PLANNING.SHOW.CHANGE_CONTACT')"
          @click.stop="emit('openEventManagerDropdown')"
        >
          <Icon icon="i-lucide-pen-line" class="size-4 shrink-0" />
        </button>
      </div>
      <div
        v-else-if="!eventManager && canManageEventManager"
        class="relative flex h-10 cursor-pointer items-center gap-3 rounded-lg hover:bg-n-alpha-2 print:hidden"
        @click.stop="emit('openEventManagerDropdown')"
      >
        <DropdownSearch
          v-if="showEventManagerDropdown"
          :options="formattedAgents"
          :selected-item="selectedEventManager"
          has-thumbnail
          :is-searching="searchingAgents"
          :title="t('PLANNING.EVENTS.SELECT_MANAGER')"
          :no-search-result="t('PLANNING.SHOW.NO_AGENTS_FOUND')"
          :input-placeholder="t('PLANNING.SHOW.SEARCH_AGENTS')"
          @select="toggleEventManager"
          @close="emit('closeEventManagerDropdown')"
          @search="emit('searchEventManager', $event)"
        />
        <div
          class="flex size-10 shrink-0 items-center justify-center rounded-full bg-n-alpha-2"
        >
          <Icon icon="i-lucide-user-plus" class="size-4 text-n-slate-10" />
        </div>
        <p class="m-0 text-sm leading-5 text-black">
          {{ t('PLANNING.SHOW.ASSIGN_EVENT_MANAGER') }}
        </p>
      </div>
      <div v-else-if="!eventManager" class="flex h-10 items-center gap-3">
        <p class="m-0 text-sm leading-5 text-black">
          {{ t('PLANNING.SHOW.NO_EVENT_MANAGER') }}
        </p>
      </div>

      <div
        v-if="hasCollaborators"
        class="group/collabs flex flex-col gap-4"
      >
        <DropdownSearch
          v-if="showCollaboratorsDropdown"
          :options="collaboratorOptions"
          :selected-items="selectedCollaborators"
          multiple
          has-thumbnail
          :title="t('PLANNING.SHOW.SELECT_COLLABORATORS')"
          :no-search-result="t('PLANNING.SHOW.NO_COLLABORATORS_FOUND')"
          :input-placeholder="t('PLANNING.SHOW.SEARCH_COLLABORATORS')"
          @select="toggleCollaborator"
          @close="showCollaboratorsDropdown = false"
        />
        <div
          v-for="(user, index) in collaboratorUsers"
          :key="`user-${user.id}`"
          class="relative flex items-center gap-3"
        >
          <button
            v-if="canManageCollaborators && index === 0"
            class="absolute left-1.5 top-1/2 z-10 hidden h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full bg-woot-500 hover:bg-woot-300 text-white group-hover/collabs:inline-flex print:hidden"
            :title="t('PLANNING.SHOW.CHANGE_CONTACT')"
            @click.stop="openCollaboratorEditor"
          >
            <Icon icon="i-lucide-pen-line" class="size-4 shrink-0" />
          </button>
          <Avatar
            :src="user.thumbnail"
            :name="user.name"
            :size="40"
            :status="user.availability_status"
            rounded-full
          />
          <div class="min-w-0 flex-1">
            <p
              class="m-0 truncate text-sm font-semibold leading-5 text-n-slate-12"
            >
              {{ user.name }}
            </p>
            <p
              v-if="user.email"
              class="m-0 truncate text-xs leading-4 text-black"
            >
              {{ user.email }}
            </p>
            <a
              v-if="user.phone_number"
              :href="`tel:${user.phone_number}`"
              class="m-0 block truncate text-xs leading-4 text-black hover:text-woot-500"
            >
              {{ user.phone_number }}
            </a>
          </div>
          <span
            class="shrink-0 rounded-full bg-woot-50 px-2.5 py-0.5 text-xs font-medium text-woot-600 dark:bg-woot-900/20 dark:text-woot-300"
          >
            {{ t('PLANNING.SHOW.COLLABORATOR') }}
          </span>
        </div>
        <div
          v-for="(team, index) in collaboratorTeams"
          :key="`team-${team.id}`"
          class="flex flex-col gap-2"
        >
          <div class="relative flex items-center gap-3">
            <button
              v-if="
                canManageCollaborators &&
                !collaboratorUsers.length &&
                index === 0
              "
              class="absolute left-1.5 top-1/2 z-10 hidden h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full bg-woot-500 hover:bg-woot-300 text-white group-hover/collabs:inline-flex print:hidden"
              :title="t('PLANNING.SHOW.CHANGE_CONTACT')"
              @click.stop="openCollaboratorEditor"
            >
              <Icon icon="i-lucide-pen-line" class="size-4 shrink-0" />
            </button>
            <button
              type="button"
              class="flex min-w-0 flex-1 items-center gap-3 rounded-lg p-0 text-left hover:bg-n-alpha-2"
              @click.stop="toggleTeam(team.id)"
            >
              <Avatar :name="team.name" :size="40" rounded-full />
              <div class="min-w-0 flex-1">
                <p
                  class="m-0 truncate text-sm font-semibold leading-5 text-n-slate-12"
                >
                  {{ team.name }}
                </p>
              </div>
              <Icon
                :icon="
                  isTeamExpanded(team.id)
                    ? 'i-lucide-chevron-up'
                    : 'i-lucide-chevron-down'
                "
                class="size-4 shrink-0 text-n-slate-10"
              />
              <span
                class="shrink-0 rounded-full bg-woot-50 px-2.5 py-0.5 text-xs font-medium text-woot-600 dark:bg-woot-900/20 dark:text-woot-300"
              >
                {{ t('PLANNING.SHOW.COLLABORATOR') }}
              </span>
            </button>
          </div>
          <div
            v-if="isTeamExpanded(team.id)"
            class="ml-6 flex flex-col gap-3"
          >
            <div
              v-if="isTeamLoading(team.id)"
              class="flex h-8 items-center gap-2 text-n-slate-10"
            >
              <span class="i-lucide-loader-2 size-4 animate-spin" />
            </div>
            <template v-else>
              <div
                v-for="member in getTeamMembers(team.id)"
                :key="`team-${team.id}-member-${member.id}`"
                class="flex items-center gap-3"
              >
                <Avatar
                  :src="member.thumbnail"
                  :name="member.name"
                  :size="32"
                  :status="member.availability_status"
                  rounded-full
                />
                <div class="min-w-0 flex-1">
                  <p
                    class="m-0 truncate text-sm font-semibold leading-5 text-n-slate-12"
                  >
                    {{ member.name }}
                  </p>
                  <p
                    v-if="member.email"
                    class="m-0 truncate text-xs leading-4 text-black"
                  >
                    {{ member.email }}
                  </p>
                  <a
                    v-if="member.phone_number"
                    :href="`tel:${member.phone_number}`"
                    class="m-0 block truncate text-xs leading-4 text-black hover:text-woot-500"
                    @click.stop
                  >
                    {{ member.phone_number }}
                  </a>
                </div>
              </div>
              <p
                v-if="!getTeamMembers(team.id).length"
                class="m-0 text-xs leading-4 text-n-slate-10"
              >
                {{ t('PLANNING.SHOW.NO_TEAM_MEMBERS') }}
              </p>
            </template>
          </div>
        </div>
      </div>
      <div
        v-else-if="canManageCollaborators"
        class="relative flex h-10 cursor-pointer items-center gap-3 rounded-lg hover:bg-n-alpha-2 print:hidden"
        @click.stop="openCollaboratorEditor"
      >
        <DropdownSearch
          v-if="showCollaboratorsDropdown"
          :options="collaboratorOptions"
          :selected-items="selectedCollaborators"
          multiple
          has-thumbnail
          :title="t('PLANNING.SHOW.SELECT_COLLABORATORS')"
          :no-search-result="t('PLANNING.SHOW.NO_COLLABORATORS_FOUND')"
          :input-placeholder="t('PLANNING.SHOW.SEARCH_COLLABORATORS')"
          @select="toggleCollaborator"
          @close="showCollaboratorsDropdown = false"
        />
        <div
          class="flex size-10 shrink-0 items-center justify-center rounded-full bg-n-alpha-2"
        >
          <Icon icon="i-lucide-user-plus" class="size-4 text-n-slate-10" />
        </div>
        <p class="m-0 text-sm leading-5 text-black">
          {{ t('PLANNING.SHOW.ASSIGN_COLLABORATORS') }}
        </p>
      </div>
    </div>
  </EventSidebarSection>
</template>
